import { isUfeEnvProduction } from '@ufe/shared/constants/env';
import localeUtils from 'utils/LanguageLocale';
import anaUtils from 'analytics/utils';

/** Returns the environment-specific Rokt placement identifier for the given placement name. */
export const getRoktIdentifier = name => {
    return {
        confirmation: isUfeEnvProduction ? 'prod.rokt.confirmation' : 'stg.rokt.confirmation',
        payments: isUfeEnvProduction ? 'prod.rokt.payments' : 'stg.rokt.payments'
    }[name];
};

/** Returns true when Rokt SDK+ is enabled and the user is in the US. */
export const isSdkPlusEnabled = () => !!(Sephora.configurationSettings?.smn?.roktSDKPlus?.isEnabled && localeUtils.isUS());

/**
 * Fires a Rokt SDK+ page view event via mParticle.logPageView.
 * No-ops when SDK+ is disabled, user is not in the US, or mParticle is not ready.
 * Safe to call from useEffect and componentDidMount.
 *
 * @param {object} options
 * @param {string} options.pagetype   - Rokt pagetype value (e.g. 'Auth', 'MyAccount')
 * @param {string} options.screenname - Rokt screenname value (e.g. 'Sign In')
 * @param {string} options.title      - Hardcoded page title per Rokt spec
 */
export const fireRoktPageView = ({ pagetype, screenname, title }) => {
    if (!isSdkPlusEnabled() || typeof window.mParticle?.ready !== 'function') {
        return;
    }

    const attributes = {
        screenname,
        pagetype,
        title,
        url: window.location.toString(),
        referringpage: document.referrer
    };

    window.mParticle.ready(() => {
        window.mParticle.logPageView('page view', attributes);
    });
};

const buildRoktIdentityRequest = async profile => {
    const emailSource = (profile?.login || profile?.email || '').toLowerCase().trim();
    const userIdentities = {
        ...(emailSource && {
            // eslint-disable-next-line camelcase
            email_sha256: await anaUtils.hashString(emailSource)
        }),
        ...(profile?.profileId && { customerid: profile.profileId })
    };

    return Object.keys(userIdentities).length ? { userIdentities } : null;
};

const setRoktProfileAttributes = (result, profile) => {
    const user = result.getUser();

    if (!user) {
        Sephora.logger.warn('[Rokt] mParticle identity failed, proceeding without user attributes');

        return;
    }

    if (profile?.firstName) {
        user.setUserAttribute('firstname', profile.firstName);
    }

    if (profile?.lastName) {
        user.setUserAttribute('lastname', profile.lastName);
    }

    user.setUserAttribute('customertype', 'logged_in');

    const biAccount = profile?.beautyInsiderAccount;

    if (biAccount?.loyaltyTierName) {
        user.setUserAttribute('loyaltytier', biAccount.loyaltyTierName.toLowerCase());
    }

    if (biAccount?.biAccountId) {
        user.setUserAttribute('loyaltyid', biAccount.biAccountId);
    }
};

/**
 * Fires a Rokt SDK+ identity login event (login or registration).
 * Use when a user explicitly authenticates.
 */
export const fireRoktLogin = profile => {
    if (!isSdkPlusEnabled() || typeof window.mParticle?.ready !== 'function') {
        return;
    }

    window.mParticle.ready(async () => {
        const identityRequest = await buildRoktIdentityRequest(profile);

        if (identityRequest) {
            window.mParticle.Identity.login(identityRequest, result => setRoktProfileAttributes(result, profile));
        }
    });
};

/**
 * Fires a Rokt SDK+ identity identify event.
 * Use when a user identity is already known at startup (no explicit login).
 */
export const fireRoktIdentify = profile => {
    if (!isSdkPlusEnabled() || typeof window.mParticle?.ready !== 'function') {
        return;
    }

    window.mParticle.ready(async () => {
        const identityRequest = await buildRoktIdentityRequest(profile);

        if (identityRequest) {
            window.mParticle.Identity.identify(identityRequest, result => setRoktProfileAttributes(result, profile));
        }
    });
};

/**
 * Fires a Rokt SDK+ identity logout event.
 */
export const fireRoktLogout = () => {
    if (!isSdkPlusEnabled() || typeof window.mParticle?.ready !== 'function') {
        return;
    }

    window.mParticle.ready(() => {
        window.mParticle.Identity.logout({});
    });
};
